import { useState, useEffect, forwardRef, useImperativeHandle, useRef, useCallback } from "react";

//Components
import { Grid, IconButton } from "@mui/material";
import { Table, Tooltip, InputTags, ToggleSwitch } from "@gpstrackit/react-controls";
import MultiLevelSelector from 'Components/SelectorMultiLevel'
import { TableButtons } from '../TableButtons'
import SearchInput from './SearchInput';
import moment from 'moment';
//Preferences
import Preferences from 'Core/containers/Preferences';
import { getPMUser } from 'Core/data/Helpers';
//Language
import {
    useIntl,
} from "react-intl";

//Icons
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterIcon from '@mui/icons-material/FilterList';
import DensityIcon from '@mui/icons-material/LineWeight';
//Styles
import './dataView.scss';

import {
    useSelector,
    useDispatch,
} from "react-redux";

//Actions
import {
    getDataViewRequest,
    genericExportSend,
    clearRedux,
} from "Redux/actions";

//Helpers
import { validateEpochDate } from 'Core/data/Helpers';

const DataView = forwardRef((props, ref) => {
    const dispatch = useDispatch();
    const { messages } = useIntl();
    const tableRef = useRef();
    const preferencesRef = useRef();
    const {
        columns,
        topheight,
        noSplit,
        entity = "default-list",
        tableButtons,
        options,
        graphqlFunction,
        graphqlSort,
        graphqlConditions,
        customQuery,
        defaultSort,
        name,
        noFilters,
        height,
        showError,
        noSearch,
        noRefresh,
        noDensity,
        infiniteScroll,
        nextToken,
        externalLoading,
        externalRefresh,
        onChangePage,
        onChangeRowsPerPage,
        onChangeSearch,
        noTags,
        extraFilters,
        dinamicQuery,
        placeholderSearch,
        searchConditions,
        searchOptionValue,
        searchOptionLabel,
        customSearch,
        extraField,
        noAutomaticSearch,
        disableItemSelection,
        menuOptions,
        onClickMenu,
        toolbarFullWidth,
        hidePagination,
        noPreferences,
        noTagsPreferences,
        extraButton,
        dataGridExport,
        isPXSource = false,
        isDynamo = false,
        pxAccount,
        flexEnd,
        fixSearchBar,
        styleSearchBar,
        exportName,
        noSearchOptions,
        onExternalSorting,
        simpleSearch,
        filterSelected,
        valuesPerPage,
        defaultRows,
        searchFields,
        noMoreItemsMessage,
        generateExport,
        exportLimit,
        customColumn,
        openSearchCustomOptions,
        minWidthButtonSection,
        onlyEnterSearch,
        searchBarWidth, //percent value
        splitNoDevices,
        maxTags,
    } = props;

    //Redux States
    const data = useSelector(state => state.commonRedux[entity]);
    const errorDataView = useSelector(state => state.commonRedux.errorDataView);
    const loadingPreference = useSelector(state => state.preferencesRedux?.loadingPreference);
    const loading = useSelector(state => state.commonRedux[`loading-${entity}`]);
    const { change } = useSelector(state => state.commonRedux);
    const pmUsers = useSelector(state => state.generalRedux.pmUsers);

    //Local States
    const [checked, setChecked] = useState(false);
    const [rows, setRows] = useState(10);
    const [page, setPage] = useState(0);
    const [sort, setSort] = useState(defaultSort || { field: "id", order: "ASC" });
    const [conditions, setConditions] = useState([]);
    const [density, setDensity] = useState(3);
    const [selectedItems, setSelectedItems] = useState([]);
    const [tags, setTags] = useState([])
    const [valueSearch, setValueSearch] = useState("");
    const [graphql, setGraphql] = useState("");

    const getData = (newPage, newRows, newSort, newConditions, newColumns, filters) => {

        let currentPage = page;
        let currentRows = rows;
        let currentSort = sort;
        let currentConditions = conditions;
        let currentColumns = columns;
        let params = customQuery || "id"
        cleanSelectedItems();
        if (newColumns) currentColumns = newColumns
        if (!infiniteScroll) {
            if (dinamicQuery) {
                currentColumns.filter((item) => item.show).map((column) => {
                    params += " "
                    let fields = column.field && column.field.split(",") || []
                    fields.map(f => {
                        let array = f.split(".")
                        if (array.length > 1) {
                            let text = "";
                            let closeText = "";
                            if (params.includes(array[0])) {
                                let divider = params.split(`${array[0]} { `)
                                params = divider[0] + ` ${array[0]} { ` + `${array[1]} ` + divider[1]
                            } else {
                                for (let i = 0; i < array.length; i++) {
                                    if (!params.includes(array[0])) {
                                        text += array[i]
                                    }
                                    if (i < array.length - 1) {
                                        text += " { "
                                        closeText += " } "
                                    }
                                }
                            }
                            params += text + closeText
                        }
                        else params += array[0]
                    })
                })
            }

            let query = `
                query ${graphqlFunction} ($conditions: [${graphqlConditions}], $sort: [${graphqlSort}], $limit: Int, $offset: Int) {
                    result: ${graphqlFunction} (conditions: $conditions, sort: $sort, limit: $limit, offset: $offset) {
                        items {
                            ${params}
                        }
                        total
                    }
                }
            `;

            if (newPage || newPage === 0) {
                currentPage = newPage
                setPage(newPage);
            }
            if (newRows) {
                currentRows = newRows
                setRows(newRows);
                tableRef?.current?.setPage(1);
                currentPage = 0;
                setPage(0);
            }
            if (newSort) {
                if (!Array.isArray(newSort)) {
                    currentSort = { field: newSort.key, order: newSort?.order?.toUpperCase() }
                } else {
                    currentSort = newSort
                }

                setSort(currentSort);
            } else if (sort.key) {
                currentSort = { field: sort.key, order: sort?.order?.toUpperCase() }
                setSort(currentSort);
            }

            let sortField = currentColumns?.find(item => item.field === currentSort.field || item.key === currentSort.field);
            if (currentColumns && currentColumns.length > 0 && !sortField) {
                console.warn("The current sort field is not present in the columns, resetting to default sort");
                currentSort = [{ field: "id", order: "ASC" }]
                setSort(currentSort);

            }
            if (newConditions) {
                currentConditions = newConditions;
                conditionsHandler(newConditions, newRows);
                tableRef?.current?.setPage(1);
                currentPage = 0;
                setPage(0);
            }
            setGraphql(query);

            if (filters && !filters.filterValidation) {
                return
            }
            if (!props.externalData) {
                dispatch(getDataViewRequest(entity, query, currentPage, currentRows, currentSort, currentConditions))
            }
        }
    }

    const handleChange = () => {
        setChecked(!checked);
        changePreferences({ split: !checked });
    }

    const densityHandler = useCallback(() => {
        tableRef.current?.onChangeDensity();
    })

    const conditionsHandler = (newconditions, newRows) => {
        setConditions(newconditions);
        let preferences = { conditions: newconditions }
        if (newRows)
            preferences.rows = newRows
        changePreferences(preferences);
    }

    const changePreferences = useCallback(async (newValues) => {
        if (name) {
            const oldPreferences = preferencesRef?.current?.getPreferences("Other", name) || {}
            if (!_.isEqual(newValues, oldPreferences)) {
                const currentPreferences = { ...oldPreferences, ...newValues }
                await preferencesRef.current.savePreference("Other", name, currentPreferences)
            }
        }
    })

    const onChangeColumns = useCallback((newColums) => {
        props.onChangeColumns && props.onChangeColumns([...newColums]);
        let prefColums = newColums.map(x => {
            return { key: x?.key, show: x?.show, width: x?.width, locked: x?.locked, resize: x?.resize, icon: x?.icon, export: x?.export, functionString: x?.functionString, code: x?.code }
        })
        changePreferences({ columns: prefColums });
    })

    const onSelectedItems = (items) => {
        setSelectedItems(items);
        props.onSelectedItems && props.onSelectedItems(items);
    }

    const cleanSelectedItems = () => {
        tableRef?.current?.setSelectedItems([]);
        props.onSelectedItems && props.onSelectedItems([]);
        setSelectedItems([]);
    }

    const exportHandler = (type, columnsVisibility) => {
        let currentType = type === 'CSV' ? 'Csv' : 'Pdf';
        if (generateExport) {
            generateExport(currentType)
        } else {
            if (!dataGridExport) {
                let headers = []

                if (columnsVisibility === 'All')
                    headers = columns.filter(x => x.export).map(x => { return { header: x.title, column: x.key, functionString: x.functionString } });
                else
                    headers = columns.filter(x => x.show && x.export).map(x => { return { header: x.title, column: x.key, functionString: x.functionString } });

                let query = props?.query || graphql;
                let currentFilters = {
                    conditions: conditions,
                    limit: props?.externalDataTotal || data?.total || 0,
                    offset: 0,
                    sort,
                }

                let limit = exportLimit || 100
                dispatch(genericExportSend(
                    currentType,
                    query,
                    props.conditions || currentFilters,
                    headers,
                    { unit: 'pt', size: 'A4', orientation: 'portrait', fontSize: 12 },
                    isPXSource,
                    isDynamo,
                    pxAccount,
                    exportName,
                    null,
                    limit,
                ));
            } else {
                exportCSV(props.externalData, columnsVisibility);
            }
        }

    }

    const getExportFileName = useCallback((ext) => {
        let date = new Date()
        return `${props.name}_${date.getFullYear()}${date.getMonth()}${date.getDay()}${date.getHours()}${date.getMinutes()}.` + ext
    })

    //to format the export values like date that could come on milliseconds
    const getExportFormatVal = (val, format, timezone) => {
        let formatted = "";
        switch (format) {
            case "date":
                formatted = moment(val).format("MMM DD YYYY")
                break;
            case "datetime":
                formatted = moment(val).format("MMM DD YYYY h:mm:ss a")
                break;
            case "time":
                formatted = moment(val).format("h:mm:ss a")
                break;
            case "timezone":
                formatted = moment(val).tz(timezone).format("YYYY-MM-DD HH:mm:ss z")
                break;
            default:
                formatted = val; // if the is no format return the same valew
                break;
        }

        return formatted;
    }

    const exportCSV = (data, columnsVisibility = 'All') => {

        let csvContent = '';
        const headerRow = [];
        let show = false
        columns &&
            columns.forEach((item) => {
                if (item.export) headerRow.push(item.title || item.field);
            });

        csvContent += headerRow.join(',') + '\n';

        data.forEach((item) => {
            const row = [];
            columns &&
                columns.forEach((col) => {
                    show = false
                    if (columnsVisibility === 'all')
                        show = true
                    else
                        show = col.show
                    if (col.export && show) {
                        let val = '';
                        val = _.get(item, col.exportCell);
                        val = val !== -1 ? val : ''

                        if (!col.exportCell && col.exportValue) {
                            val = col.exportValue(item)
                        }

                        if (col.exportFormat && val) {
                            let timezone = item?.timezone;
                            const validDate = validateEpochDate(val)
                            val = validDate ? getExportFormatVal(val, col.exportFormat, timezone) : ''
                        }

                        if ((col.field === 'UpdatedBy' || col.field === 'CreatedBy') && val)
                            val = getPMUser(val, pmUsers);

                        row.push(val || '');
                    }
                });

            csvContent += row.join(',') + '\n';

        });

        var a = document.createElement('a');
        let mimeType = 'text/csv;encoding:utf-8';
        let fileName = getExportFileName('csv');

        if (navigator.msSaveBlob) {
            // IE10
            navigator.msSaveBlob(new Blob([csvContent], { type: mimeType }), fileName);
        } else if (URL && 'download' in a) {
            // HTML5 A[download]
            a.href = URL.createObjectURL(new Blob([csvContent], { type: mimeType }));
            a.setAttribute('download', fileName);
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        } else {
            location.href = 'data:application/octet-stream,' + encodeURIComponent(csvContent);
        }
    }

    useImperativeHandle(ref, () => ({
        getData: getData,
        conditions: conditions,
        conditionsHandler: conditionsHandler,
        setTags: setTags,
        setValueSearch: setValueSearch,
        cleanSelectedItems: cleanSelectedItems,
        selectedItems: selectedItems,
        setSort: setSort,
        setRows: setRows
    }));

    useEffect(() => {
        if (valueSearch && !noAutomaticSearch) {
            const timer = setTimeout(() => {
                tableRef.current.setPage(1)
                tableRef.current.setSelectedItems([]);
                let currenttags = tags;
                if (!noTags) {
                    if (valueSearch) props.onChangeConditions && props.onChangeConditions([...currenttags, valueSearch]);
                    else props.onChangeConditions && props.onChangeConditions([...currenttags]);
                }
            }, 500)
            return () => clearTimeout(timer);
        }
    }, [valueSearch])

    useEffect(() => {
        if (!infiniteScroll && ((props.externalData && props?.externalDataTotal >= 0) || (data?.total >= 0))) {
            props.onChangeTotal && props.onChangeTotal(props?.externalDataTotal || data?.total);
        }

        if (errorDataView) {
            showError && showError(errorDataView);
            dispatch(clearRedux("COMMON", { errorDataView: null }))
        }

    }, [data?.total, props?.externalDataTotal, errorDataView])

    useEffect(() => {
        if (change) {
            let columnsTranslated = columns.map((column) => ({ ...column, title: column.code ? messages[column.code] : column.title }))
            onChangeColumns(columnsTranslated);
            dispatch(clearRedux("COMMON", { change: false }))
        }
    }, [change])

    return (
        <Preferences
            ref={preferencesRef}
            preferencesKey={name}
            onInitialPreferences={(preferences) => {
                let visibleColumns = null;
                let prefConditions = [];
                //Preferences for columns
                if (!noPreferences && preferences?.columns) {
                    let newColumns = [...columns]
                    let reset = false;
                    preferences.columns.forEach((col, index) => {
                        let indexCol = newColumns.findIndex(x => (x?.key == col?.key))
                        const nncol = columns[indexCol];
                        if (indexCol === -1) {
                            reset = true;
                            return;
                        }
                        let newCol = { ...newColumns[indexCol], ...col }
                        newCol.key = newColumns[indexCol].key;
                        newCol.field = newColumns[indexCol].field;

                        // functionString need to be readed always from theal column not from preferences
                        newCol.functionString = newColumns[indexCol].functionString
                        newCol.render = newColumns[indexCol].render
                        newCol.title = newColumns[indexCol].title
                        newCol.code = newColumns[indexCol].code
                        newColumns.splice(indexCol, 1)
                        newColumns.splice(index, 0, newCol)

                    })
                    if (reset)
                        newColumns = [...columns];
                    // repositioning colum
                    let positionColumns = newColumns.filter((item) => item?.position)
                    positionColumns?.forEach((colum, index) => {
                        let indexCol = newColumns.findIndex(x => (x?.key == colum?.key))
                        if (indexCol !== -1) {
                            newColumns.splice(indexCol, 1)
                            newColumns.splice(colum.position, 0, colum)
                        }
                    })
                    visibleColumns = newColumns?.filter((item) => item?.show)
                    props.onChangeColumns && props.onChangeColumns(newColumns);
                }
                //Preferences for density
                if (!noPreferences && preferences?.density) {
                    setDensity(preferences?.density);
                    tableRef?.current?.onChangeDensity(preferences?.density)
                }
                //Preferences for conditions
                if (!noPreferences && preferences?.conditions) {
                    prefConditions = preferences.conditions;
                    if (noTagsPreferences) {

                        prefConditions = preferences.conditions.filter(x => !x.tags);

                        //to clean the sub tab preferences
                        prefConditions = prefConditions.filter(x => x.fields && x.fields[0] !== 'status');
                        prefConditions = prefConditions.filter(x => x.fields && x.fields[0] !== 'unitTypeId');
                    } else if (searchFields) {
                        prefConditions = preferences.conditions.filter(x => !x.tags);
                        let tagsConditions = preferences.conditions.filter(x => x.tags);
                        if (tagsConditions.length > 0) {
                            if (tagsConditions[0].fields.length > 1)
                                tagsConditions[0].fields = searchFields
                            prefConditions.push(tagsConditions[0])
                        }
                    }

                    setConditions(prefConditions);
                }
                if (!noPreferences && preferences?.split) {
                    setChecked(preferences?.split)
                }

                if (!noPreferences && preferences?.rows) {
                    setRows(preferences?.rows)

                    tableRef?.current?.setRowsPerPage(preferences?.rows)
                } else if (defaultRows) {
                    setRows(defaultRows)
                    tableRef?.current?.setRowsPerPage(defaultRows)
                }

                if (!noPreferences && preferences?.sort) {
                    setSort(preferences?.sort)
                    tableRef?.current?.setSort(preferences?.sort)
                }
                if (!noPreferences) {
                    props.onChangeConditions && props.onChangeConditions(null, prefConditions, data?.total, visibleColumns, preferences?.rows, preferences?.sort, null, true);
                }
            }}
        >
            <Grid container className="container-dataview">
                <Grid item xs={minWidthButtonSection ? 1 : 4}>
                    <div className="container-dataview-buttons">
                        {
                            options?.length > 0 &&
                            <MultiLevelSelector
                                label={messages['actions']}
                                options={options || []}
                                selectedItems={selectedItems}
                                position="right"
                            />
                        }
                        {!noFilters &&
                            <Tooltip title={messages['advanced_filters']}>
                                <IconButton
                                    style={{ padding: 0, marginRight: 15, color: 'black' }}
                                    onClick={() => props.onFilters && props.onFilters()}
                                    size="large">
                                    <FilterIcon style={{ color: 'black', fontSize: 20 }} className={filterSelected ? "button-filter-selected" : "button-filter-no-selected"} />
                                </IconButton>
                            </Tooltip>
                        }

                        {
                            !noRefresh &&
                            <Tooltip title={messages['refresh']}>
                                <IconButton
                                    style={{ padding: 0, marginRight: 15 }}
                                    onClick={() => externalRefresh ? externalRefresh() : getData()}
                                    size="large">
                                    <RefreshIcon style={{ color: 'black', fontSize: 20 }} />
                                </IconButton>
                            </Tooltip>
                        }
                        {
                            !noDensity &&
                            <Tooltip title={messages['density']}>
                                <IconButton
                                    style={{ padding: 0, marginRight: 10 }}
                                    onClick={() => {
                                        let currentDensity = tableRef.current?.density;
                                        let value = currentDensity === 3 ? 1 : currentDensity + 1;
                                        changePreferences({ density: value });
                                        densityHandler();
                                    }}
                                    size="large">
                                    <DensityIcon style={{ color: 'black', fontSize: 20 }} />
                                </IconButton>
                            </Tooltip>
                        }
                        <TableButtons
                            lockedColumns
                            messages={messages}
                            buttons={tableButtons || []}
                            columns={columns || []}
                            onChangeColumns={(items, show) => {
                                if (show) getData(null, null, null, null, items)
                                onChangeColumns(items);
                            }}
                            tableRef={tableRef}
                            noPDF
                            externalExport={(event, columnsVisibility) => exportHandler(event, columnsVisibility)}
                        />
                    </div>
                </Grid>
                <Grid item xs={minWidthButtonSection ? 11 : 8} style={{ justifyContent: 'end' }}>
                    <div style={{ display: !flexEnd ? 'flex' : 'flex-end', alignItems: fixSearchBar ? 'center' : '', justifyContent: 'end' }}>
                        {
                            !noSplit &&
                            <div className="split-action">
                                <ToggleSwitch
                                    id="splitAction"
                                    large
                                    blueToggle
                                    optionLabels={[messages['split'], messages['no_split']]}
                                    checked={checked}
                                    onChange={handleChange}
                                />
                            </div>
                        }
                        {
                            extraFilters && extraFilters()
                        }
                        {
                            !noSearch &&
                            <div style={{ width: searchBarWidth ? `${searchBarWidth}%` : "70%", zIndex: 4 }}>
                                <SearchInput
                                    graphqlFunction={graphqlFunction}
                                    graphqlConditions={graphqlConditions}
                                    searchConditions={searchConditions}
                                    searchOptionValue={searchOptionValue}
                                    searchOptionLabel={searchOptionLabel}
                                    customSearch={customSearch}
                                    extraField={extraField}
                                    value={tags}
                                    onChange={(tags, search, key) => {
                                        const items = (tags || []).map((item) => {
                                            let obj = JSON.parse(JSON.stringify(item));
                                            if (typeof obj.id === "string") {
                                                obj.id = obj.id.replaceAll(",", " ")
                                                obj.id = obj?.id?.trim();
                                            }
                                            if (typeof obj.name === "string") {
                                                obj.name = obj?.name?.trim();
                                            }
                                            return obj;
                                        });

                                        const cleanItems = items.filter((item) => {
                                            const labelValue = typeof item?.[searchOptionLabel] === "string"
                                                ? item?.[searchOptionLabel]?.trim()
                                                : item?.[searchOptionLabel];

                                            if (typeof item?.id === "number") return true;
                                            if (typeof item?.id === "string" && item?.id?.trim()) return true;
                                            if (typeof item?.name === "string" && item?.name?.trim()) return true;
                                            if (typeof labelValue === "string" && labelValue) return true;
                                            return false;
                                        });

                                        setTags(cleanItems);
                                        !search && props.onChangeConditions && props.onChangeConditions(cleanItems, null, null, null, null, null, key);
                                    }}
                                    noSplit={noSplit}
                                    checked={checked}
                                    placeholder={placeholderSearch || messages['search']}
                                    messages={messages}
                                    style={styleSearchBar}
                                    isDisabled={externalLoading || loading || loadingPreference}
                                    noSearchOptions={noSearchOptions}
                                    conditions={conditions}
                                    conditionsHandler={conditionsHandler}
                                    openSearchCustomOptions={openSearchCustomOptions}
                                    onlyEnterSearch={onlyEnterSearch}
                                    splitNoDevices={splitNoDevices}
                                    maxTags={maxTags}
                                />
                            </div>
                        }
                        {
                            simpleSearch &&
                            <InputTags
                                tags={tags}
                                setTags={(tags) => {
                                    if (!noTags) {
                                        props.onChangeConditions && props.onChangeConditions(tags);
                                        setTags(tags);
                                    }
                                }}
                                placeholder={placeholderSearch || messages['search']}
                                valueSearch={valueSearch}
                                setValueSearch={(value) => {
                                    onChangeSearch && onChangeSearch(value)
                                    setValueSearch(value)
                                }}
                                noClear
                            />
                        }
                        {
                            extraButton && extraButton()
                        }
                    </div>
                </Grid>
                <Grid item xs={12} style={{ marginTop: 10 }}>
                    <Table
                        ref={tableRef}
                        loading={externalLoading || loading || loadingPreference}
                        messages={messages}
                        data={props.externalData || data?.items || []}
                        total={props.externalDataTotal || data?.total || 0}
                        columns={columns}
                        topheight={topheight}
                        onChangeColumns={(items) => onChangeColumns(items)}
                        onChangePage={(page) => {
                            if (onChangePage) {
                                onChangePage(nextToken, page);
                                setPage(page - 1)
                            } else getData(page - 1);
                        }}
                        onChangeRowsPerPage={(rows) => {
                            if (onChangeRowsPerPage) {
                                onChangeRowsPerPage(rows);
                                setRows(rows);
                            } else getData(null, rows)

                            changePreferences({ rows: rows });
                        }}
                        onChangeDensity={(density) => {

                            setDensity(density);
                            //changePreferences({ density: density });
                        }}
                        onChangeSort={(sort) => {
                            changePreferences({ sort: sort });
                            setSort(sort);
                            !props.externalData && getData(null, null, sort); //normal
                            props.externalData && onExternalSorting && onExternalSorting(sort); //with external sorting
                            props.externalData && onExternalSorting && tableRef?.current?.setPage(1); //to reset the page if have more than one, when the sort change
                        }}
                        defaultDensity={density}
                        defaultRowsPerPage={rows}
                        hideAllInPagination
                        lockedColumns
                        height={height}
                        infiniteScroll={infiniteScroll}
                        nextToken={nextToken}
                        onSelectedItems={onSelectedItems}
                        disableItemSelection={disableItemSelection}
                        menuOptions={menuOptions}
                        onClickMenu={onClickMenu}
                        hidePagination={hidePagination}
                        noMoreItemsMessage={noMoreItemsMessage || messages['no_more_items']}
                        valuesPerPage={valuesPerPage}
                        customColumn={customColumn}
                    />
                </Grid>
            </Grid>
        </Preferences>
    );
})

export default DataView;